import { supabase } from './supabase';

// ===================== TYPES =====================

type QueueOperation = 'INSERT' | 'UPDATE' | 'DELETE';

interface QueuedMutation {
  id: string;
  table: string;
  operation: QueueOperation;
  recordId: string;
  data?: Record<string, any>;
  timestamp: number;
  metadata?: {
    summary?: string;
    performedBy?: string;
  };
  /** ID de la mutation parente (pour chaînage FK INSERT → INSERT) */
  parentMutationId?: string;
  /** IDs des mutations enfants qui dépendent de celle-ci */
  childMutationIds?: string[];
  /** Timestamp updated_at attendu côté serveur (pour détection conflits cross-device).
   *  Si le serveur a un updated_at différent, un autre appareil a modifié la même donnée. */
  expectedUpdatedAt?: string | null;
  /** Origine : quel appareil a créé cette mutation (pour le diagnostic de conflits) */
  deviceId?: string;
}

interface ConflictInfo {
  mutationId: string;
  type: 'REFERENCED_BY_PENDING' | 'TARGET_DELETED' | 'TARGET_MODIFIED' | 'FOREIGN_KEY_VIOLATION' | 'DUPLICATE' | 'SERVER_ERROR' | 'TIME_SLOT_CONFLICT' | 'TIME_SLOT_OVERLAP' | 'PATIENT_OVERLAP' | 'TIME_SLOT_TAKEN' | 'PARENT_FAILED' | 'RLS_BLOCKED' | 'CROSS_DEVICE_CONFLICT';
  table: string;
  operation: QueueOperation;
  recordId: string;
  description: string;
  relatedMutations: string[];
  serverMessage?: string;
  resolution?: 'RETRY' | 'DISCARD' | 'FORCE';
  /** Données serveur actuelles en cas de conflit cross-device (pour fusion manuelle) */
  serverData?: Record<string, any>;
}

// ===================== CLÉS DE RÉFÉRENCES CROISÉES =====================

const FOREIGN_KEY_MAP: Record<string, { column: string; refTable: string }[]> = {
  rendez_vous: [
    { column: 'patient_id', refTable: 'patients' },
    { column: 'medecin_id', refTable: 'medecins' },
    { column: 'secretaire_id', refTable: 'secretaires' },
    { column: 'sage_femme_id', refTable: 'sage_femmes' },
  ],
  consultations: [
    { column: 'patient_id', refTable: 'patients' },
    { column: 'medecin_id', refTable: 'medecins' },
    { column: 'rendez_vous_id', refTable: 'rendez_vous' },
  ],
  prescriptions: [
    { column: 'patient_id', refTable: 'patients' },
    { column: 'medecin_id', refTable: 'medecins' },
    { column: 'consultation_id', refTable: 'consultations' },
  ],
  resultats_analyses: [
    { column: 'patient_id', refTable: 'patients' },
    { column: 'dossier_analyse_id', refTable: 'dossiers_analyses' },
  ],
  dossiers_analyses: [
    { column: 'patient_id', refTable: 'patients' },
    { column: 'medecin_id', refTable: 'medecins' },
  ],
  suivis_patients: [
    { column: 'patient_id', refTable: 'patients' },
  ],
  bons_laboratoire: [
    { column: 'patient_id', refTable: 'patients' },
    { column: 'medecin_id', refTable: 'medecins' },
  ],
  disponibilites_medecins: [
    { column: 'medecin_id', refTable: 'medecins' },
  ],
  fiches_surveillance: [
    { column: 'patient_id', refTable: 'patients' },
  ],
  fiches_soins: [
    { column: 'patient_id', refTable: 'patients' },
  ],
  patient_assurances: [
    { column: 'patient_id', refTable: 'patients' },
    { column: 'assurance_id', refTable: 'assurances' },
  ],
};

// ===================== STOCKAGE (localStorage + Web Locks) =====================

const SYNC_QUEUE_KEY = 'csf-sync-queue';
const CONFLICTS_KEY = 'csf-sync-conflicts';
const SYNC_LOCK_NAME = 'csf-sync-lock';

// Identifiant unique de l'appareil (persistant dans localStorage)
function getDeviceId(): string {
  try {
    let deviceId = localStorage.getItem('csf-device-id');
    if (!deviceId) {
      deviceId = `device-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('csf-device-id', deviceId);
    }
    return deviceId;
  } catch {
    return `device-${Date.now()}`;
  }
}

// BroadcastChannel pour coordination multi-onglets
const SYNC_CHANNEL = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('csf-sync-coordination')
  : null;

// Web Locks API : vrai mutex inter-onglets
async function acquireLock(): Promise<boolean> {
  if (!('locks' in navigator)) {
    // Fallback pour navigateurs sans Web Locks API
    if (localStorage.getItem('csf-sync-legacy-lock') === 'true') return false;
    localStorage.setItem('csf-sync-legacy-lock', 'true');
    return true;
  }

  try {
    // Tenter d'acquérir le lock avec un timeout
    const result = await new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), 5000);

      navigator.locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, (lock) => {
        clearTimeout(timeout);
        if (lock) {
          // Garder le lock jusqu'à ce qu'on le relâche explicitement
          return new Promise<void>((keepLock) => {
            (window as any).__csfSyncRelease = keepLock;
            resolve(true);
          });
        }
        resolve(false);
      }).catch(() => resolve(false));
    });
    return result;
  } catch {
    return false;
  }
}

function releaseLock(): void {
  if (!('locks' in navigator)) {
    localStorage.removeItem('csf-sync-legacy-lock');
    return;
  }
  const release = (window as any).__csfSyncRelease;
  if (release) {
    (window as any).__csfSyncRelease = null;
    release();
  }
}

function getQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(SYNC_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: QueuedMutation[]): void {
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    console.warn('[SyncQueue] localStorage plein — queue non sauvegardée');
  }
}

function getConflicts(): ConflictInfo[] {
  try {
    const raw = localStorage.getItem(CONFLICTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveConflicts(conflicts: ConflictInfo[]): void {
  try {
    localStorage.setItem(CONFLICTS_KEY, JSON.stringify(conflicts));
  } catch {
    console.warn('[SyncQueue] Impossible de sauvegarder les conflits');
  }
}

// ===================== FILE D'ATTENTE (avec merge intelligent) =====================

export function enqueueMutation(
  table: string,
  operation: QueueOperation,
  recordId: string,
  data?: Record<string, any>,
  metadata?: QueuedMutation['metadata'],
  parentMutationId?: string,
  expectedUpdatedAt?: string | null,
): string {
  const queue = getQueue();
  const mutationId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  const deviceId = getDeviceId();

  if (operation === 'UPDATE') {
    // MERGE intelligent : fusionner avec les UPDATEs précédents du même enregistrement
    const existingUpdateIdx = queue.findIndex(
      (m) => m.table === table && m.recordId === recordId && m.operation === 'UPDATE',
    );

    if (existingUpdateIdx !== -1) {
      // Fusionner les data : le nouvel appel écrase les champs présents,
      // mais on garde les champs de l'ancien UPDATE qui ne sont pas dans le nouveau
      const existing = queue[existingUpdateIdx];
      const mergedData = { ...(existing.data || {}), ...(data || {}) };

      queue[existingUpdateIdx] = {
        ...existing,
        data: mergedData,
        timestamp: Date.now(),
        metadata: metadata || existing.metadata,
        expectedUpdatedAt: expectedUpdatedAt || existing.expectedUpdatedAt,
        deviceId,
      };
      saveQueue(queue);

      // Mettre à jour le lien parent si applicable
      if (parentMutationId && !existing.childMutationIds?.includes(parentMutationId)) {
        // L'UPDATE fait référence à une mutation parente — on enregistre le lien inverse
        const parent = queue.find((m) => m.id === parentMutationId);
        if (parent) {
          parent.childMutationIds = [...(parent.childMutationIds || []), mutationId];
        }
      }

      return existing.id;
    }

    // Nouvel UPDATE : écraser les anciens UPDATE/DELETE pour éviter les doublons
    const cleanedQueue = queue.filter(
      (m) => !(m.table === table && m.recordId === recordId && m.operation !== 'INSERT'),
    );
    const mutation: QueuedMutation = {
      id: mutationId,
      table,
      operation,
      recordId,
      data,
      timestamp: Date.now(),
      metadata,
      parentMutationId,
      expectedUpdatedAt,
      deviceId,
    };
    cleanedQueue.push(mutation);
    saveQueue(cleanedQueue);

    // Lien parent → enfant
    if (parentMutationId) {
      const parent = cleanedQueue.find((m) => m.id === parentMutationId);
      if (parent) {
        parent.childMutationIds = [...(parent.childMutationIds || []), mutationId];
        saveQueue(cleanedQueue);
      }
    }

    return mutationId;
  }

  if (operation === 'DELETE') {
    // DELETE écrase tout ce qui concerne cet enregistrement
    let cleanedQueue = queue.filter(
      (m) => !(m.table === table && m.recordId === recordId),
    );
    const mutation: QueuedMutation = {
      id: mutationId,
      table,
      operation,
      recordId,
      timestamp: Date.now(),
      metadata,
      parentMutationId,
      expectedUpdatedAt,
      deviceId,
    };
    cleanedQueue.push(mutation);
    saveQueue(cleanedQueue);
    return mutationId;
  }

  // INSERT : toujours ajouter, sans écrasement
  const mutation: QueuedMutation = {
    id: mutationId,
    table,
    operation,
    recordId,
    data,
    timestamp: Date.now(),
    metadata,
    parentMutationId,
    expectedUpdatedAt,
    deviceId,
  };
  queue.push(mutation);

  // Lien parent → enfant
  if (parentMutationId) {
    const parent = queue.find((m) => m.id === parentMutationId);
    if (parent) {
      parent.childMutationIds = [...(parent.childMutationIds || []), mutationId];
    }
  }

  saveQueue(queue);
  return mutationId;
}

export function getQueueSize(): number {
  return getQueue().length;
}

export function getPendingMutations(): QueuedMutation[] {
  return getQueue();
}

// ===================== DÉTECTION DE CONFLITS =====================

function detectTimeSlotConflicts(queue: QueuedMutation[]): {
  conflictIds: Set<string>;
  conflicts: ConflictInfo[];
} {
  const conflictIds = new Set<string>();
  const conflicts: ConflictInfo[] = [];

  const rdvInserts = queue
    .filter((m) => m.table === 'rendez_vous' && m.operation === 'INSERT' && m.data)
    .sort((a, b) => a.timestamp - b.timestamp);

  if (rdvInserts.length < 2) return { conflictIds, conflicts };

  const getDoctorSummary = (m: QueuedMutation): string => {
    return m.metadata?.summary || 'ce médecin';
  };

  for (let i = 0; i < rdvInserts.length; i++) {
    if (conflictIds.has(rdvInserts[i].id)) continue;

    const a = rdvInserts[i];
    const aMedecin = a.data?.medecin_id;
    const aDate = a.data?.date_rdv;
    const aDebut = a.data?.heure_debut;
    const aFin = a.data?.heure_fin;
    const aPatient = a.data?.patient_id;

    if (!aMedecin || !aDate || !aDebut || !aFin) continue;

    for (let j = i + 1; j < rdvInserts.length; j++) {
      if (conflictIds.has(rdvInserts[j].id)) continue;

      const b = rdvInserts[j];
      const bMedecin = b.data?.medecin_id;
      const bDate = b.data?.date_rdv;
      const bDebut = b.data?.heure_debut;
      const bFin = b.data?.heure_fin;
      const bPatient = b.data?.patient_id;

      if (!bMedecin || !bDate || !bDebut || !bFin) continue;

      const sameMedecin = aMedecin === bMedecin;
      const sameDate = aDate === bDate;
      const samePatient = aPatient && bPatient && aPatient === bPatient;

      if (sameMedecin && sameDate && aDebut === bDebut) {
        conflictIds.add(b.id);
        conflicts.push({
          mutationId: b.id,
          type: 'TIME_SLOT_CONFLICT',
          table: 'rendez_vous',
          operation: 'INSERT',
          recordId: b.recordId,
          description: `Conflit de créneau : un autre rendez-vous hors-ligne est déjà prévu avec ${getDoctorSummary(b)} le ${formatDateDisplay(aDate)} à ${aDebut}.`,
          relatedMutations: [a.id],
        });
        continue;
      }

      if (sameMedecin && sameDate && timeRangesOverlap(aDebut, aFin, bDebut, bFin)) {
        conflictIds.add(b.id);
        conflicts.push({
          mutationId: b.id,
          type: 'TIME_SLOT_OVERLAP',
          table: 'rendez_vous',
          operation: 'INSERT',
          recordId: b.recordId,
          description: `Chevauchement de créneau : le rendez-vous ${bDebut}-${bFin} chevauche un autre RDV hors-ligne avec ${getDoctorSummary(b)} le ${formatDateDisplay(aDate)} (${aDebut}-${aFin}).`,
          relatedMutations: [a.id],
        });
        continue;
      }

      if (samePatient && sameDate && timeRangesOverlap(aDebut, aFin, bDebut, bFin)) {
        conflictIds.add(b.id);
        conflicts.push({
          mutationId: b.id,
          type: 'PATIENT_OVERLAP',
          table: 'rendez_vous',
          operation: 'INSERT',
          recordId: b.recordId,
          description: `Conflit patient : ce patient a déjà un autre rendez-vous hors-ligne le ${formatDateDisplay(aDate)} de ${aDebut} à ${aFin}.`,
          relatedMutations: [a.id],
        });
      }
    }
  }

  return { conflictIds, conflicts };
}

/**
 * Détecte les chaînes FK : un INSERT qui référence un autre INSERT pas encore joué.
 * Ex: création d'un patient hors-ligne, puis création d'un RDV pour ce patient.
 * Si l'INSERT parent échoue, les enfants sont automatiquement en conflit PARENT_FAILED.
 */
function detectFkChains(queue: QueuedMutation[]): ConflictInfo[] {
  const conflicts: ConflictInfo[] = [];
  const insertsById = new Map<string, QueuedMutation>();

  // Indexer tous les INSERTs par leur recordId
  for (const m of queue) {
    if (m.operation === 'INSERT') {
      insertsById.set(m.recordId, m);
    }
  }

  // Pour chaque INSERT, vérifier si ses FK pointent vers un autre INSERT
  for (const m of queue) {
    if (m.operation !== 'INSERT' || !m.data) continue;

    const fks = FOREIGN_KEY_MAP[m.table] || [];
    for (const fk of fks) {
      const refValue = m.data[fk.column];
      if (!refValue) continue;

      // Chercher un INSERT pour la table référencée avec ce recordId
      const refInsert = insertsById.get(refValue);
      if (refInsert && refInsert.table === fk.refTable) {
        // Chaîne FK détectée : cet INSERT dépend de refInsert
        // On enregistre le lien parent → enfant
        if (!refInsert.childMutationIds?.includes(m.id)) {
          refInsert.childMutationIds = [...(refInsert.childMutationIds || []), m.id];
        }
        if (!m.parentMutationId) {
          m.parentMutationId = refInsert.id;
        }
      }
    }
  }

  return conflicts;
}

function detectConflicts(queue: QueuedMutation[]): {
  safeMutations: QueuedMutation[];
  conflicts: ConflictInfo[];
  needsReorder: boolean;
} {
  const conflicts: ConflictInfo[] = [];
  const conflictIds = new Set<string>();

  // 0. Détecter les chaînes FK (INSERT → INSERT)
  detectFkChains(queue);

  // 1. Détecter les DELETE qui ont des INSERT référents
  const deleteMutations = queue.filter((m) => m.operation === 'DELETE');
  const insertMutations = queue.filter((m) => m.operation === 'INSERT');

  for (const del of deleteMutations) {
    const referencingTables = Object.entries(FOREIGN_KEY_MAP)
      .filter(([, fks]) => fks.some((fk) => fk.refTable === del.table))
      .map(([table]) => table);

    const relatedInserts = insertMutations.filter((ins) =>
      referencingTables.includes(ins.table),
    );

    for (const ins of relatedInserts) {
      const fks = FOREIGN_KEY_MAP[ins.table] || [];
      for (const fk of fks) {
        if (fk.refTable === del.table && ins.data && ins.data[fk.column] === del.recordId) {
          conflictIds.add(del.id);
          conflictIds.add(ins.id);
          conflicts.push({
            mutationId: del.id,
            type: 'REFERENCED_BY_PENDING',
            table: del.table,
            operation: del.operation,
            recordId: del.recordId,
            description: `Impossible de supprimer ce${getTableLabel(del.table)} : un${getTableLabel(ins.table)} en attente de création y fait référence.`,
            relatedMutations: [ins.id],
          });
        }
      }
    }
  }

  // 2. Détection des créneaux horaires
  const preTimeSlotSafe = queue.filter((m) => !conflictIds.has(m.id));
  const { conflictIds: timeSlotIds, conflicts: timeSlotConflicts } = detectTimeSlotConflicts(preTimeSlotSafe);

  for (const id of timeSlotIds) conflictIds.add(id);
  for (const c of timeSlotConflicts) conflicts.push(c);

  // 3. Mutations safe
  const safeMutations = queue.filter((m) => !conflictIds.has(m.id));

  const needsReorder = safeMutations.some(
    (m, i) => i > 0 && getOperationPriority(m.operation) < getOperationPriority(safeMutations[i - 1].operation),
  );

  return { safeMutations, conflicts, needsReorder };
}

function timeRangesOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
  return start1 < end2 && start2 < end1;
}

function formatDateDisplay(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getOperationPriority(op: QueueOperation): number {
  switch (op) {
    case 'INSERT': return 1;
    case 'UPDATE': return 2;
    case 'DELETE': return 3;
  }
}

function getTableLabel(table: string): string {
  const labels: Record<string, string> = {
    medecins: 'médecin',
    patients: 'patient',
    secretaires: 'secrétaire',
    rendez_vous: 'rendez-vous',
    consultations: 'consultation',
    prescriptions: 'prescription',
    dossiers_analyses: 'dossier d\'analyses',
    resultats_analyses: 'résultat d\'analyses',
    suivis_patients: 'suivi patient',
    bons_laboratoire: 'bon de laboratoire',
    disponibilites_medecins: 'créneau de disponibilité',
    medicaments: 'médicament',
    actualites: 'actualité',
    laborantins: 'laborantin',
    sage_femmes: 'sage-femme',
    infirmiers: 'infirmier',
    comptables: 'comptable',
    fiches_surveillance: 'fiche de surveillance',
    fiches_soins: 'fiche de soins',
    dossiers_roses: 'dossier rose',
    patient_assurances: 'assurance patient',
    file_attente: 'entrée file d\'attente',
    assurances: 'assurance',
    patients_temporaires: 'patient temporaire',
  };
  return labels[table] || table;
}

// ===================== EXÉCUTION =====================

async function replayMutation(mutation: QueuedMutation): Promise<{ success: boolean; errorMessage?: string; errorCode?: string; serverData?: Record<string, any> }> {
  try {
    switch (mutation.operation) {
      case 'INSERT': {
        if (!mutation.data) return { success: true };
        const insertData = { ...mutation.data };
        if (mutation.recordId && mutation.recordId !== 'pending' && !mutation.recordId.startsWith('pending-')) {
          insertData.id = mutation.recordId;
        } else {
          // ID temporaire : on le retire pour que Supabase génère l'UUID
          delete insertData.id;
        }

        if (mutation.table === 'rendez_vous') {
          const preCheck = await verifyTimeSlotFree(
            insertData.medecin_id,
            insertData.date_rdv,
            insertData.heure_debut,
            insertData.heure_fin,
            insertData.patient_id,
          );
          if (!preCheck.free) {
            return { success: false, errorMessage: preCheck.reason || 'Créneau déjà réservé', errorCode: 'TIME_SLOT_TAKEN' };
          }
        }

        const { error, data: inserted } = await supabase.from(mutation.table).insert(insertData).select('id').maybeSingle();
        if (error) {
          return { success: false, errorMessage: error.message, errorCode: error.code };
        }

        // Mettre à jour le recordId si Supabase a généré un ID
        if (inserted?.id && (mutation.recordId === 'pending' || mutation.recordId.startsWith('pending-'))) {
          mutation.recordId = inserted.id;
          // Mettre à jour les enfants qui référencent l'ancien ID
          updateChildReferences(mutation, inserted.id);
        }

        if (mutation.table === 'rendez_vous' && inserted?.id) {
          const postCheck = await verifyTimeSlotFree(
            insertData.medecin_id,
            insertData.date_rdv,
            insertData.heure_debut,
            insertData.heure_fin,
            insertData.patient_id,
            inserted.id,
          );
          if (!postCheck.free) {
            await supabase.from('rendez_vous').delete().eq('id', inserted.id);
            return { success: false, errorMessage: postCheck.reason || 'Créneau pris par une autre synchronisation', errorCode: 'TIME_SLOT_TAKEN' };
          }
        }

        return { success: true };
      }

      case 'UPDATE': {
        if (!mutation.data) return { success: true };

        // ===== DÉTECTION CONFLIT CROSS-DEVICE =====
        // Si on avait mémorisé le updated_at attendu, vérifier que personne
        // d'autre n'a modifié cet enregistrement pendant qu'on était offline.
        if (mutation.expectedUpdatedAt) {
          try {
            const { data: currentRow, error: fetchErr } = await supabase
              .from(mutation.table)
              .select('updated_at')
              .eq('id', mutation.recordId)
              .maybeSingle();

            if (!fetchErr && currentRow) {
              const serverUpdatedAt = currentRow.updated_at;
              if (serverUpdatedAt && serverUpdatedAt !== mutation.expectedUpdatedAt) {
                // Conflit cross-device : un autre appareil a modifié ce même enregistrement
                const deviceLabel = mutation.deviceId
                  ? ` (appareil ${mutation.deviceId.substring(0, 8)})`
                  : '';

                // Récupérer les données serveur actuelles pour aider à la fusion
                const { data: serverFull } = await supabase
                  .from(mutation.table)
                  .select('*')
                  .eq('id', mutation.recordId)
                  .maybeSingle();

                return {
                  success: false,
                  errorMessage: `CONFLIT CROSS-DEVICE : "${mutation.metadata?.summary || mutation.recordId}" a été modifié par un autre appareil pendant votre utilisation hors-ligne${deviceLabel}. Vos modifications (${Object.keys(mutation.data).join(', ')}) sont en conflit avec la version serveur.`,
                  errorCode: 'CROSS_DEVICE_CONFLICT',
                  serverData: serverFull,
                };
              }
            }
          } catch {
            // Si la table n'a pas de colonne updated_at, on ignore la vérification
          }
        }

        const { error } = await supabase
          .from(mutation.table)
          .update(mutation.data)
          .eq('id', mutation.recordId);
        if (error) {
          return { success: false, errorMessage: error.message, errorCode: error.code };
        }
        return { success: true };
      }

      case 'DELETE': {
        const { error } = await supabase
          .from(mutation.table)
          .delete()
          .eq('id', mutation.recordId);
        if (error) {
          return { success: false, errorMessage: error.message, errorCode: error.code };
        }
        return { success: true };
      }

      default:
        return { success: true };
    }
  } catch (err: any) {
    return { success: false, errorMessage: err?.message || 'Erreur inconnue' };
  }
}

/** Met à jour les références des mutations enfants après qu'un INSERT parent a reçu son ID serveur */
function updateChildReferences(parentMutation: QueuedMutation, newRecordId: string): void {
  const queue = getQueue();
  let updated = false;

  for (const m of queue) {
    if (m.parentMutationId === parentMutation.id && m.data) {
      // Trouver quelle FK pointe vers le parent
      const fks = FOREIGN_KEY_MAP[m.table] || [];
      for (const fk of fks) {
        if (fk.refTable === parentMutation.table && m.data[fk.column] === parentMutation.recordId) {
          m.data[fk.column] = newRecordId;
          updated = true;
        }
      }
    }
  }

  if (updated) saveQueue(queue);
}

// ===================== VÉRIFICATION CRÉNEAU =====================

async function verifyTimeSlotFree(
  medecinId: string | undefined,
  dateRdv: string | undefined,
  heureDebut: string | undefined,
  heureFin: string | undefined,
  patientId?: string,
  excludeId?: string,
): Promise<{ free: boolean; reason?: string }> {
  if (!medecinId || !dateRdv || !heureDebut || !heureFin) {
    return { free: true };
  }

  try {
    let exactQuery = supabase
      .from('rendez_vous')
      .select('id, heure_debut, heure_fin, patient_id')
      .eq('medecin_id', medecinId)
      .eq('date_rdv', dateRdv)
      .eq('heure_debut', heureDebut)
      .neq('statut', 'annule');

    if (excludeId) exactQuery = exactQuery.neq('id', excludeId);

    const { data: exactConflicts } = await exactQuery;
    if (exactConflicts && exactConflicts.length > 0) {
      return {
        free: false,
        reason: `Ce créneau horaire (${dateRdv} à ${heureDebut}) est déjà réservé pour ce médecin par un autre utilisateur.`,
      };
    }

    let overlapQuery = supabase
      .from('rendez_vous')
      .select('id, heure_debut, heure_fin, patient_id')
      .eq('medecin_id', medecinId)
      .eq('date_rdv', dateRdv)
      .neq('statut', 'annule');

    if (excludeId) overlapQuery = overlapQuery.neq('id', excludeId);

    const { data: allDayRdvs } = await overlapQuery;
    if (allDayRdvs) {
      const hasOverlap = allDayRdvs.some((rdv) =>
        timeRangesOverlap(heureDebut, heureFin, rdv.heure_debut, rdv.heure_fin),
      );
      if (hasOverlap) {
        return {
          free: false,
          reason: `Le créneau ${heureDebut}-${heureFin} chevauche un autre rendez-vous déjà confirmé pour ce médecin le ${dateRdv}.`,
        };
      }
    }

    if (patientId) {
      let patientQuery = supabase
        .from('rendez_vous')
        .select('id, heure_debut, heure_fin, medecin_id')
        .eq('patient_id', patientId)
        .eq('date_rdv', dateRdv)
        .neq('statut', 'annule');

      if (excludeId) patientQuery = patientQuery.neq('id', excludeId);

      const { data: patientRdvs } = await patientQuery;
      if (patientRdvs) {
        const hasPatientOverlap = patientRdvs.some((rdv) =>
          timeRangesOverlap(heureDebut, heureFin, rdv.heure_debut, rdv.heure_fin),
        );
        if (hasPatientOverlap) {
          return {
            free: false,
            reason: `Ce patient a déjà un autre rendez-vous le ${dateRdv} qui chevauche ce créneau (${heureDebut}-${heureFin}).`,
          };
        }
      }
    }

    return { free: true };
  } catch (err: any) {
    console.warn('[SyncQueue] Erreur vérification créneau:', err?.message);
    return { free: true };
  }
}

// ===================== TRAITEMENT PRINCIPAL =====================

export async function processSyncQueue(): Promise<{
  success: number;
  failed: number;
  remaining: number;
  conflicts: ConflictInfo[];
}> {
  if (!navigator.onLine) {
    return { success: 0, failed: 0, remaining: getQueueSize(), conflicts: getConflicts() };
  }

  const locked = await acquireLock();
  if (!locked) {
    return { success: 0, failed: 0, remaining: getQueueSize(), conflicts: getConflicts() };
  }

  try {
    const queue = getQueue();
    if (queue.length === 0) {
      return { success: 0, failed: 0, remaining: 0, conflicts: getConflicts() };
    }

    const { safeMutations, conflicts: newConflicts } = detectConflicts(queue);

    let allConflicts = getConflicts();
    for (const c of newConflicts) {
      if (!allConflicts.find((existing) => existing.mutationId === c.mutationId)) {
        allConflicts.push(c);
      }
    }
    saveConflicts(allConflicts);

    // Trier : INSERT d'abord (en respectant les chaînes parent → enfant), puis UPDATE, puis DELETE
    const ordered = topologicalSort(safeMutations);

    const failedMutations: QueuedMutation[] = [];
    let successCount = 0;
    const failedParentIds = new Set<string>();

    for (const mutation of ordered) {
      // Si le parent de cette mutation a échoué, marquer comme PARENT_FAILED
      if (mutation.parentMutationId && failedParentIds.has(mutation.parentMutationId)) {
        failedParentIds.add(mutation.id);
        const parentConflict: ConflictInfo = {
          mutationId: mutation.id,
          type: 'PARENT_FAILED',
          table: mutation.table,
          operation: mutation.operation,
          recordId: mutation.recordId,
          description: `Impossible de ${mutation.operation === 'DELETE' ? 'supprimer' : mutation.operation === 'UPDATE' ? 'modifier' : 'créer'} "${mutation.metadata?.summary || mutation.recordId}" : l'opération parente (${getTableLabel(getParentTable(mutation))}) a échoué. Résolvez d'abord le conflit parent.`,
          relatedMutations: [mutation.parentMutationId],
        };

        if (!allConflicts.find((c) => c.mutationId === mutation.id)) {
          allConflicts.push(parentConflict);
          saveConflicts(allConflicts);
        }
        continue;
      }

      const result = await replayMutation(mutation);

      if (result.success) {
        successCount++;
        allConflicts = allConflicts.filter((c) => c.mutationId !== mutation.id);

        // Mettre à jour les références des enfants si l'ID a changé
        // (déjà fait dans replayMutation via updateChildReferences)
      } else {
        const errorType = categorizeServerError(result.errorMessage || '', result.errorCode || '', mutation);

        if (errorType === 'PARENT_FAILED' || errorType === 'FOREIGN_KEY_VIOLATION' || errorType === 'DUPLICATE' || errorType === 'TIME_SLOT_TAKEN' || errorType === 'TIME_SLOT_CONFLICT' || errorType === 'TIME_SLOT_OVERLAP' || errorType === 'PATIENT_OVERLAP' || errorType === 'RLS_BLOCKED' || errorType === 'CROSS_DEVICE_CONFLICT') {
          failedParentIds.add(mutation.id);

          // Propager l'échec aux enfants
          if (mutation.childMutationIds && mutation.childMutationIds.length > 0) {
            for (const childId of mutation.childMutationIds) {
              failedParentIds.add(childId);
              allConflicts.push({
                mutationId: childId,
                type: 'PARENT_FAILED',
                table: 'unknown',
                operation: 'INSERT',
                recordId: childId,
                description: `Opération dépendante bloquée : l'opération parente "${mutation.metadata?.summary || mutation.recordId}" a échoué. Résolvez d'abord le conflit parent.`,
                relatedMutations: [mutation.id],
              });
            }
          }

          const conflict: ConflictInfo = {
            mutationId: mutation.id,
            type: errorType,
            table: mutation.table,
            operation: mutation.operation,
            recordId: mutation.recordId,
            description: formatServerError(result.errorMessage || '', mutation),
            relatedMutations: [],
            serverMessage: result.errorMessage,
            serverData: result.serverData,
          };

          if (!allConflicts.find((c) => c.mutationId === mutation.id)) {
            allConflicts.push(conflict);
          }
        } else {
          // Erreur temporaire → on réessaiera
          failedMutations.push(mutation);
        }
      }
    }

    // Sauvegarder l'état final
    const finalQueue = [...failedMutations];
    saveQueue(finalQueue);
    saveConflicts(allConflicts);

    // Notifier les autres onglets qu'une synchro vient d'avoir lieu
    if (SYNC_CHANNEL) {
      try {
        SYNC_CHANNEL.postMessage({
          type: 'SYNC_COMPLETED',
          success: successCount,
          failed: failedMutations.length,
          remaining: finalQueue.length,
          conflictCount: allConflicts.length,
        });
      } catch {
        // Silencieux
      }
    }

    return {
      success: successCount,
      failed: failedMutations.length,
      remaining: finalQueue.length,
      conflicts: allConflicts,
    };
  } finally {
    releaseLock();
  }
}

/** Tri topologique : respecte l'ordre parent → enfant pour les chaînes FK */
function topologicalSort(mutations: QueuedMutation[]): QueuedMutation[] {
  const byId = new Map(mutations.map((m) => [m.id, m]));
  const sorted: QueuedMutation[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();

  function visit(m: QueuedMutation) {
    if (visited.has(m.id)) return;
    if (visiting.has(m.id)) return; // Cycle → ignorer
    visiting.add(m.id);

    // Visiter le parent d'abord
    if (m.parentMutationId && byId.has(m.parentMutationId)) {
      const parent = byId.get(m.parentMutationId)!;
      visit(parent);
    }

    visiting.delete(m.id);
    visited.add(m.id);
    sorted.push(m);
  }

  for (const m of mutations) {
    visit(m);
  }

  return sorted;
}

function getParentTable(mutation: QueuedMutation): string {
  const queue = getQueue();
  const parent = queue.find((m) => m.id === mutation.parentMutationId);
  return parent?.table || 'inconnue';
}

function categorizeServerError(
  errorMessage: string,
  errorCode: string,
  mutation: QueuedMutation,
): ConflictInfo['type'] {
  const msg = errorMessage.toLowerCase();
  const code = (errorCode || '').toLowerCase();

  if (code === 'cross_device_conflict' || code === 'CROSS_DEVICE_CONFLICT' || msg.includes('cross-device') || msg.includes('conflit cross-device')) {
    return 'CROSS_DEVICE_CONFLICT';
  }
  if (code === '42501' || msg.includes('permission denied') || msg.includes('policy') || msg.includes('rls')) {
    return 'RLS_BLOCKED';
  }
  if (msg.includes('créneau') || msg.includes('déjà réservé') || msg.includes('chevauche')) {
    return 'TIME_SLOT_TAKEN';
  }
  if (code === '23503' || msg.includes('foreign key') || msg.includes('violates foreign key') || msg.includes('fk_')) {
    return 'FOREIGN_KEY_VIOLATION';
  }
  if (code === '23505' || msg.includes('duplicate') || msg.includes('unique') || msg.includes('already exists') || msg.includes('déjà exist')) {
    return 'DUPLICATE';
  }
  return 'SERVER_ERROR';
}

function formatServerError(errorMessage: string, mutation: QueuedMutation): string {
  const label = getTableLabel(mutation.table);
  const summary = mutation.metadata?.summary || mutation.recordId;
  const action = mutation.operation === 'DELETE' ? 'supprimer' : mutation.operation === 'UPDATE' ? 'modifier' : 'créer';

  const msg = errorMessage.toLowerCase();

  if (msg.includes('créneau') || msg.includes('déjà réservé') || msg.includes('chevauche')) {
    return errorMessage;
  }
  if (msg.includes('permission denied') || msg.includes('policy') || msg.includes('rls')) {
    return `Impossible de ${action} "${summary}" : vous n'avez pas les droits nécessaires (restriction RLS). Contactez un administrateur.`;
  }
  if (msg.includes('foreign key') || msg.includes('fk_') || msg.includes('23503')) {
    return `Impossible de ${action} "${summary}" : cet élément est référencé par d'autres données (contrainte de base de données).`;
  }
  if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('already exists') || msg.includes('23505')) {
    return `Impossible de ${action} "${summary}" : un enregistrement identique existe déjà.`;
  }
  return `Erreur lors de ${action === 'supprimer' ? 'la suppression' : action === 'modifier' ? 'la modification' : 'la création'} de "${summary}" : ${errorMessage}`;
}

// ===================== RÉSOLUTION DES CONFLITS =====================

export function getUnresolvedConflicts(): ConflictInfo[] {
  return getConflicts().filter((c) => !c.resolution);
}

export async function resolveConflict(
  conflictId: string,
  resolution: 'RETRY' | 'DISCARD' | 'FORCE',
): Promise<void> {
  const conflicts = getConflicts();
  const conflictIndex = conflicts.findIndex((c) => c.mutationId === conflictId);
  if (conflictIndex === -1) return;

  const conflict = conflicts[conflictIndex];

  if (resolution === 'DISCARD') {
    // Supprimer la mutation ET ses enfants de la queue
    const queue = getQueue();
    const mutation = queue.find((m) => m.id === conflictId);
    const childIds = mutation?.childMutationIds || [];
    const allIdsToRemove = [conflictId, ...childIds];

    saveQueue(queue.filter((m) => !allIdsToRemove.includes(m.id)));
    saveConflicts(conflicts.filter((c) => !allIdsToRemove.includes(c.mutationId)));
    return;
  }

  if (resolution === 'RETRY') {
    // Remettre dans la queue pour réessayer (déjà présent si non retiré)
    const queue = getQueue();
    const mutation = queue.find((m) => m.id === conflictId);
    if (mutation) {
      conflicts.splice(conflictIndex, 1);
      saveConflicts(conflicts);
    } else {
      conflicts[conflictIndex].resolution = 'DISCARD';
      saveConflicts(conflicts);
    }
    return;
  }

  if (resolution === 'FORCE') {
    const queue = getQueue();
    const mutation = queue.find((m) => m.id === conflictId);

    if (!mutation) {
      conflicts[conflictIndex].resolution = 'DISCARD';
      saveConflicts(conflicts);
      return;
    }

    try {
      if (mutation.operation === 'DELETE' && conflict.type === 'FOREIGN_KEY_VIOLATION') {
        await forceDeleteWithCascade(mutation);
      } else if (conflict.type === 'RLS_BLOCKED') {
        // Pour RLS, FORCE ne peut pas contourner — on informe l'utilisateur
        conflicts[conflictIndex].description = `Action bloquée par les règles de sécurité (RLS). Vous n'avez pas les permissions nécessaires pour forcer cette opération. Contactez un administrateur.`;
        conflicts[conflictIndex].resolution = 'DISCARD';
        saveConflicts(conflicts);
        return;
      } else if (mutation.operation === 'INSERT' || mutation.operation === 'UPDATE') {
        await supabase.from(mutation.table).upsert({ ...mutation.data, id: mutation.recordId });
      }

      // Succès → retirer la mutation et ses enfants de la queue
      const childIds = mutation.childMutationIds || [];
      const allIdsToRemove = [conflictId, ...childIds];

      saveConflicts(conflicts.filter((c) => !allIdsToRemove.includes(c.mutationId)));
      saveQueue(queue.filter((m) => !allIdsToRemove.includes(m.id)));
    } catch (err: any) {
      const errorMsg = err?.message || '';
      const isRlsError = errorMsg.includes('permission') || errorMsg.includes('policy') || errorMsg.includes('42501');

      if (isRlsError) {
        conflicts[conflictIndex].description = `Échec de la résolution forcée : vous n'avez pas les droits nécessaires (restriction RLS). Contactez un administrateur.`;
      } else {
        conflicts[conflictIndex].serverMessage = errorMsg;
        conflicts[conflictIndex].description = `Échec de la résolution forcée : ${errorMsg || 'Erreur inconnue'}`;
      }
      saveConflicts(conflicts);
    }
  }
}

async function forceDeleteWithCascade(mutation: QueuedMutation): Promise<void> {
  const references = Object.entries(FOREIGN_KEY_MAP)
    .filter(([, fks]) => fks.some((fk) => fk.refTable === mutation.table));

  const rlsFailures: string[] = [];

  for (const [refTable, fks] of references) {
    for (const fk of fks) {
      if (fk.refTable === mutation.table) {
        const { error } = await supabase
          .from(refTable)
          .delete()
          .eq(fk.column, mutation.recordId);

        if (error) {
          const isRls = error.message.includes('permission') || error.message.includes('policy') || error.code === '42501';
          if (isRls) {
            rlsFailures.push(`${getTableLabel(refTable)} (${fk.column})`);
          } else {
            console.warn(`[SyncQueue] Échec cascade ${refTable}.${fk.column}:`, error.message);
          }
        }
      }
    }
  }

  if (rlsFailures.length > 0) {
    throw new Error(`Suppression cascade partiellement bloquée par RLS sur : ${rlsFailures.join(', ')}. Certains enregistrements liés n'ont pas pu être supprimés.`);
  }

  const { error } = await supabase.from(mutation.table).delete().eq('id', mutation.recordId);
  if (error) throw error;
}

// ===================== AUTO-TRIGGER AU DÉMARRAGE =====================

/**
 * À appeler au montage de l'app si on est online et qu'il y a des mutations en attente.
 * Vérifie si une synchro dormante existe (session précédente fermée avant sync).
 */
export async function syncOnStartupIfNeeded(): Promise<void> {
  if (!navigator.onLine) return;

  const pendingCount = getQueueSize();
  const conflictCount = getConflicts().length;
  const offlineLogCount = (() => {
    try {
      return JSON.parse(localStorage.getItem('csf-offline-logs') || '[]').length;
    } catch { return 0; }
  })();

  // Rejouer les logs offline d'abord
  if (offlineLogCount > 0) {
    const { replayOfflineLogs } = await import('@/lib/accessLogs');
    await replayOfflineLogs();
  }

  if (pendingCount > 0) {
    console.log(`[SyncQueue] Démarrage : ${pendingCount} mutation(s) en attente, lancement synchro...`);
    await processSyncQueue();
  }
  if (conflictCount > 0) {
    console.log(`[SyncQueue] Démarrage : ${conflictCount} conflit(s) non résolu(s)`);
  }
}

// ===================== NETTOYAGE =====================

export function clearSyncQueue(): void {
  saveQueue([]);
}

export function clearConflicts(): void {
  saveConflicts([]);
}

export function hasConflictsOrPending(): boolean {
  return getQueueSize() > 0 || getConflicts().length > 0;
}